/**
 * @since 2026-04-22
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { listOverrides } from "services/api";

const POLL_INTERVAL_MS = 5000;

export const usePendingOverrides = ({ idToken, getToken, enabled = true } = {}) => {
  const [overrides, setOverrides] = useState([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!idToken || !enabled) return;
    try {
      const response = await listOverrides({ pendingOnly: true }, idToken, getToken);
      if (cancelledRef.current) return;
      setOverrides(response?.overrides || []);
    } catch {
      // Non-critical — poll will retry.
    }
  }, [idToken, getToken, enabled]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled || !idToken) return () => {};
    const tick = async () => {
      await fetchOnce();
      if (cancelledRef.current) return;
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchOnce, idToken, enabled]);

  return { overrides, count: overrides.length, refetch: fetchOnce };
};

export default usePendingOverrides;
